// Favoritas del riel de apps (puro, con tests).
//
// El riel muestra las apps que cada usuario fija con la estrella del menú
// completo. Lo que decide qué se ve vive acá para poder probarlo sin montar
// el layout: el orden, que una favorita sin permiso no aparezca y el tope.

export const FAVORITES_EVENT = "dumax:favorite-apps-changed";
// «Todas las apps» desde el header (móvil y tableta, donde no hay barra): la
// appsuite lo escucha, así los 34 layouts que la montan no cambian.
export const OPEN_APPS_EVENT = "dumax:open-apps";

// Las que se definieron para todos los usuarios al inicio (5-oct-2026): Mapa,
// Administración de flotilla, Reportes, Integraciones y Entrega de servicio.
// Se usan mientras el usuario no fije las suyas.
export const DEFAULT_FAVORITES = ["lite", "admin", "reportes", "fleet-integradores", "entrega-servicio-dashboard"];

// Las de omisión POR ROL (Daniel, 26-oct). Cada usuario las cambia con la
// estrella; quien ya fijó las suyas las conserva. Un rol que no esté aquí usa
// DEFAULT_FAVORITES. «ajustes» es Configuración (ahí viven las alertas).
const OWNER = "ZHHsDgLrhRyjsgBJusmR";
const ADMIN_RESELLER = "Ne6pwRmFkr4bArJN47NZ";
const ADMIN = "HiLYcf6ndou53xpXNJ02";
const MONITORISTA = "5oeO2DJfQSLFZ9nKwp73";
const WEB_SERVICE = "cFKHnQdYU9T57j5AFytp";
const DRIVER = "vRXfmHHgR5ygbKgjidpA";
const YARD_ROLES = ["cbauTfomi9m0FgncqaFR", "Hh1ILyhv8I4tmZZvwwvy", "yardAdminRole0000001", "KoNlfjdVBn7xGpZZNk9u"]; // inspector, supervisor, admin de patio, vigilante

const PLATFORM = ["lite", "admin", "reportes", "fleet-integradores", "entrega-servicio-dashboard", "ajustes"];
export const ROLE_DEFAULT_FAVORITES: Record<string, string[]> = {
  [OWNER]: PLATFORM,
  [ADMIN_RESELLER]: PLATFORM,
  [ADMIN]: ["lite", "admin", "reportes", "torre-control", "ajustes"],
  [MONITORISTA]: ["lite", "reportes", "torre-control", "ajustes"],
  [DRIVER]: ["lite", "ajustes"],
  [WEB_SERVICE]: ["lite"],
  ...Object.fromEntries(YARD_ROLES.map((r) => [r, ["truck-yard", "inspecciones", "ajustes"]])),
};

export const defaultFavoritesFor = (roleId: string | null | undefined): string[] =>
  (roleId && ROLE_DEFAULT_FAVORITES[roleId]) || DEFAULT_FAVORITES;

// Seis en un riel de 56 px de ancho caben de sobra en 761 px de alto junto con
// el botón "Todas las apps"; más que eso deja de ser "a un clic" y vuelve a ser
// un menú.
export const MAX_FAVORITES = 6;

// v2: las favoritas de omisión cambiaron antes de publicar el riel. Quien ya
// había guardado una lista (v1) la habría conservado y no vería las nuevas.
export const favoritesKey = (userId: string | null | undefined) =>
  `dumax.favoriteApps.v2.${userId || "anon"}`;

export const parseFavorites = (raw: string | null | undefined, defaults: string[] = DEFAULT_FAVORITES): string[] => {
  if (raw == null) return defaults;
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x) => typeof x === "string").slice(0, MAX_FAVORITES) : defaults;
  } catch {
    return defaults;
  }
};

export const toggleFavorite = (favs: string[], id: string): string[] =>
  favs.includes(id)
    ? favs.filter((x) => x !== id)
    : favs.length >= MAX_FAVORITES
      ? favs
      : [...favs, id];

// Las favoritas, en el orden en que se fijaron, sólo si el usuario puede usar
// esa app (la lista permitida ya viene filtrada por rol/empresa/entorno, la
// misma que pinta el menú). Una favorita sin permiso simplemente no se ve.
export const resolveFavorites = <T extends { id: string }>(favs: string[], allowed: T[]): T[] => {
  const byId = new Map(allowed.map((a) => [a.id, a]));
  return favs.map((id) => byId.get(id)).filter((a): a is T => !!a);
};
